import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Box,
  TextField,
  Chip,
  Button,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { Add as AddIcon, Send as SendIcon, Close as CloseIcon, People as PeopleIcon } from '@material-ui/icons';
import { CampfireRichEditor } from '@campfire/campfire-rich-editor';
import { EditorState } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import { AlertCard, getAlertCardColors } from '../../../../../common/cards/alert-card/AlertCard';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { FullscreenDialog } from '../../../../../common/dialogs/FullscreenDialog';
import { useEndpointFetch } from '../../../../../global/network/useEndpointFetch';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { VolunteerCommonProfile } from '../../__generated__/VolunteerCommonProfile';
import { useUser } from '../../../../../global/auth/useUser';
import { SelectVolunteersDialog } from '../../../../../common/select-volunteers/SelectVolunteersDialog';
import { GetSelectVolunteerFragment } from '../../../../../common/select-volunteers/__generated__/GetSelectVolunteerFragment';
import { getAvatarColour } from '../../../../general/user-profile/components/Avatar';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { GET_ORG_COMM_PREFERENCES } from '../../../../admin/admin-console/Communications/Preferences/admin-console-preferences-model.gql';
import { GetOrgCommPreferences } from '../../../../admin/admin-console/Communications/Preferences/__generated__/GetOrgCommPreferences';

interface VolunteerCommonProfileEmailDialogProps {
  volunteer:
    | VolunteerCommonProfile
    | { profile: { profileId: string; preferredName: string; lastName: string; avatarUrl?: string | null } };
  open: boolean;
  onClose: () => void;
}

interface SendEmailRequest {
  profileIds: string[];
  sentBy: string;
  subject: string;
  content: string;
  replyTo?: string | null;
}

interface EmailRecipient {
  profileId: string;
  volunteerId?: string;
  name: string;
  avatarUrl?: string | null;
}

const useStyles = makeStyles(() =>
  createStyles({
    sendButton: {
      textTransform: 'none',
      backgroundColor: '#6366f1',
      color: 'white',
      '&:hover': {
        backgroundColor: '#5856eb',
      },
    },
    cancelButton: {
      textTransform: 'none',
    },
    chip: {
      backgroundColor: '#f5f5f5',
      marginRight: '8px',
      marginBottom: '4px',
      '& .MuiChip-avatar': {
        width: '24px',
        height: '24px',
      },
    },
  })
);

export const VolunteerCommonProfileEmailDialog = (props: VolunteerCommonProfileEmailDialogProps) => {
  const { open, volunteer, onClose } = props;
  const { isMobile } = useCampfireTheme();
  const { setSnackbar } = useSnackbar();
  const { user } = useUser();
  const classes = useStyles();
  const [subject, setSubject] = useState('');
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recipients, setRecipients] = useState<EmailRecipient[]>([]);
  const [selectVolunteersOpen, setSelectVolunteersOpen] = useState(false);
  const [manageRecipientsOpen, setManageRecipientsOpen] = useState(false);
  const [replyTo, setReplyTo] = useState<string>('default');

  const sendEmail = useEndpointFetch<SendEmailRequest>('/vm/comms/send-email');
  const { lightTextColor } = getAlertCardColors('info');

  // Fetch tenant's reply-to email from communication config
  const { data: orgCommPreferenceData } = useCampfireQuery<GetOrgCommPreferences, undefined>(GET_ORG_COMM_PREFERENCES);
  const defaultReplyToEmail = orgCommPreferenceData?.tm.tenant.config?.communication?.replyTo || 'noreply@volaby.org';

  const MAX_DISPLAY_RECIPIENTS = 8;

  // Initialize with the current volunteer as first recipient
  React.useEffect(() => {
    if (volunteer && recipients.length === 0) {
      setRecipients([
        {
          profileId: volunteer.profile.profileId,
          volunteerId: (volunteer as any).volunteerId,
          name: `${volunteer.profile.preferredName} ${volunteer.profile.lastName}`,
          avatarUrl: volunteer.profile.avatarUrl,
        },
      ]);
    }
  }, [volunteer, recipients.length]);

  const handleAddVolunteers = (selectedVolunteers: GetSelectVolunteerFragment[]) => {
    const newRecipients = selectedVolunteers
      .filter((vol) => !recipients.some((r) => r.profileId === vol.profile.profileId))
      .map((vol) => ({
        profileId: vol.profile.profileId,
        volunteerId: vol.volunteerId,
        name: `${vol.profile.preferredName} ${vol.profile.lastName}`,
        avatarUrl: vol.profile.avatarUrl,
      }));

    setRecipients((prev) => [...prev, ...newRecipients]);
    setSelectVolunteersOpen(false);
  };

  const handleRemoveRecipient = (profileId: string) => {
    setRecipients((prev) => prev.filter((r) => r.profileId !== profileId));
  };

  const handleSubmit = async () => {
    if (!subject.trim()) {
      setSnackbar({
        message: 'Please enter a subject',
        variant: 'error',
        open: true,
      });
      return;
    }

    const contentState = editorState.getCurrentContent();
    if (!contentState.hasText()) {
      setSnackbar({
        message: 'Please enter a message',
        variant: 'error',
        open: true,
      });
      return;
    }

    if (recipients.length === 0) {
      setSnackbar({
        message: 'No recipients selected',
        variant: 'error',
        open: true,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Send single request with all profileIds
      const response = await sendEmail.run({
        profileIds: recipients.map((r) => r.profileId),
        sentBy: user.profileId,
        subject: subject,
        content: stateToHTML(contentState),
        replyTo: replyTo === 'default' ? defaultReplyToEmail : user.email,
      });

      if (response && response.ok) {
        setSnackbar({
          message: `Email sent successfully to ${recipients.length} recipient${recipients.length > 1 ? 's' : ''}`,
          variant: 'success',
          open: true,
        });
        handleClose();
      } else {
        setSnackbar({
          message: 'Failed to send email',
          variant: 'error',
          open: true,
        });
      }
    } catch (error) {
      setSnackbar({
        message: 'An error occurred while sending the email',
        variant: 'error',
        open: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSubject('');
    setEditorState(EditorState.createEmpty());
    setRecipients([]);
    onClose();
  };

  const volunteerName = recipients.length === 1 ? recipients[0].name : `${recipients.length} recipients`;

  return (
    <>
      <FullscreenDialog
        open={open}
        close={handleClose}
        title={`Email ${volunteerName}`}
        fullScreen={isMobile}
        maxWidth='sm'
        fullWidth
      >
        <Grid container spacing={2} style={{ paddingBottom: 10 }}>
          <Grid item xs={12}>
            <Box display='flex' alignItems='center' mb={2}>
              <Typography variant='body2' color='textSecondary' style={{ marginRight: '8px' }}>
                To:
              </Typography>
              <Box display='flex' flexWrap='wrap' alignItems='center' style={{ gap: '1' }}>
                {recipients.slice(0, MAX_DISPLAY_RECIPIENTS).map((recipient) => (
                  <Chip
                    key={recipient.profileId}
                    avatar={
                      recipient.avatarUrl ? (
                        <Avatar src={recipient.avatarUrl} alt={recipient.name} style={{ width: 24, height: 24 }} />
                      ) : (
                        <Avatar
                          style={{
                            width: 24,
                            height: 24,
                            backgroundColor: `#${getAvatarColour(
                              recipient.name
                                .split(' ')
                                .map((n) => n[0])
                                .join('')
                            )}`,
                            color: 'white',
                            fontSize: '10px',
                            fontWeight: 700,
                          }}
                        >
                          {recipient.name
                            .split(' ')
                            .map((n) => n[0])
                            .join('')}
                        </Avatar>
                      )
                    }
                    label={recipient.name}
                    onDelete={recipients.length > 1 ? () => handleRemoveRecipient(recipient.profileId) : undefined}
                    deleteIcon={<CloseIcon />}
                    variant='outlined'
                    className={classes.chip}
                  />
                ))}
                {recipients.length > MAX_DISPLAY_RECIPIENTS && (
                  <Chip
                    avatar={<PeopleIcon style={{ fontSize: 16 }} />}
                    label={`+${recipients.length - MAX_DISPLAY_RECIPIENTS} more`}
                    onClick={() => setManageRecipientsOpen(true)}
                    variant='outlined'
                    style={{
                      backgroundColor: '#e3f2fd',
                      color: '#1976d2',
                      cursor: 'pointer',
                      marginRight: '8px',
                      marginBottom: '4px',
                    }}
                  />
                )}
                <Button
                  size='small'
                  startIcon={<AddIcon />}
                  onClick={() => setSelectVolunteersOpen(true)}
                  style={{
                    textTransform: 'none',
                    color: '#1976d2',
                    fontSize: '14px',
                  }}
                >
                  Add more
                </Button>
              </Box>
            </Box>
          </Grid>

          {recipients.length > 10 && (
            <Grid item xs={12}>
              <AlertCard variant='info'>
                <Typography variant='subtitle2' style={{ fontSize: '14px', fontWeight: 700, color: lightTextColor }}>
                  We recommend using bulletins if you&apos;re communicating to lots of volunteers.
                </Typography>
              </AlertCard>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              label='Subject'
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              variant='outlined'
              required
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl variant='outlined' fullWidth>
              <InputLabel>Reply to</InputLabel>
              <Select value={replyTo} onChange={(e) => setReplyTo(e.target.value as string)} label='Reply to'>
                <MenuItem value='default'>Default Reply ({defaultReplyToEmail})</MenuItem>
                <MenuItem value='current'>
                  {user.firstName} {user.lastName} ({user.email})
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Box mt={2}>
              <Typography variant='body2' color='textSecondary' gutterBottom>
                Message
              </Typography>
              <CampfireRichEditor
                editorState={editorState}
                setEditorState={setEditorState}
                placeholder='Type your message here...'
              />
            </Box>
          </Grid>

          {replyTo === 'default' && (
            <Grid item xs={12}>
              <AlertCard variant='info'>
                <Typography variant='subtitle2' style={{ fontSize: '14px', fontWeight: 700, color: lightTextColor }}>
                  Replies will be sent to the default email address: {defaultReplyToEmail}
                </Typography>
              </AlertCard>
            </Grid>
          )}

          {replyTo === 'current' && (
            <Grid item xs={12}>
              <AlertCard variant='info'>
                <Typography variant='subtitle2' style={{ fontSize: '14px', fontWeight: 700, color: lightTextColor }}>
                  Replies will be sent to your email address: {user.email}
                </Typography>
              </AlertCard>
            </Grid>
          )}

          <Grid container item justify='flex-end' direction='row' style={{ padding: '8px 0', marginTop: '16px' }}>
            <Button
              onClick={handleClose}
              variant='text'
              disabled={isSubmitting}
              className={classes.cancelButton}
              style={{ marginRight: '8px' }}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              color='primary'
              disableElevation
              onClick={handleSubmit}
              disabled={isSubmitting || sendEmail.isLoading || recipients.length === 0}
              startIcon={<SendIcon />}
              className={classes.sendButton}
            >
              {isSubmitting ? 'Sending...' : 'Send'}
            </Button>
          </Grid>
        </Grid>
      </FullscreenDialog>

      {selectVolunteersOpen && (
        <SelectVolunteersDialog
          open={selectVolunteersOpen}
          activity={{
            activityId: '', // Not needed for this use case
            name: '',
            program: { programId: '', name: '' },
          }}
          ignoredVolunteerIds={[(volunteer as any).volunteerId, ...recipients.map((r) => r.volunteerId)].filter(
            Boolean
          )}
          handleClose={() => setSelectVolunteersOpen(false)}
          handleAdd={handleAddVolunteers}
          enableGroupByActivity={false}
          dialogContentText='Select additional volunteers to send this email to:'
        />
      )}

      {manageRecipientsOpen && (
        <Dialog open={manageRecipientsOpen} onClose={() => setManageRecipientsOpen(false)} maxWidth='sm' fullWidth>
          <DialogTitle>Manage Recipients ({recipients.length})</DialogTitle>
          <DialogContent>
            <List>
              {recipients.map((recipient) => (
                <ListItem key={recipient.profileId}>
                  <ListItemAvatar>
                    {recipient.avatarUrl ? (
                      <Avatar src={recipient.avatarUrl} alt={recipient.name} />
                    ) : (
                      <Avatar
                        style={{
                          backgroundColor: `#${getAvatarColour(
                            recipient.name
                              .split(' ')
                              .map((n) => n[0])
                              .join('')
                          )}`,
                          color: 'white',
                        }}
                      >
                        {recipient.name
                          .split(' ')
                          .map((n) => n[0])
                          .join('')}
                      </Avatar>
                    )}
                  </ListItemAvatar>
                  <ListItemText primary={recipient.name} />
                  <ListItemSecondaryAction>
                    <Checkbox
                      edge='end'
                      checked
                      onChange={() => handleRemoveRecipient(recipient.profileId)}
                      disabled={recipients.length === 1}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setManageRecipientsOpen(false)} variant='text' style={{ textTransform: 'none' }}>
              Done
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
